import { createBrowserRouter, Navigate } from "react-router";
import { getConfig } from "@/lib/config";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { requireAuth, requireSystemAccess } from "@/auth/require-auth";
import { TobisAdminPage } from "@/pages/tobis/TobisAdminPage";
import { TobisTitlesPage } from "@/pages/tobis/TobisTitlesPage";
import { TobisTitlePage } from "@/pages/tobis/TobisTitlePage";
import { TobisTalentsPage } from "@/pages/tobis/TobisTalentsPage";
import { TobisTalentPage } from "@/pages/tobis/TobisTalentPage";
import { TobisLabPage } from "@/pages/tobis/TobisLabPage";
import { TobisLabSessionPage } from "@/pages/tobis/TobisLabSessionPage";
import { TobisMarketPage } from "@/pages/tobis/TobisMarketPage";
import { AppShell } from "@/components/layout/AppShell";
import { HomePage } from "@/pages/HomePage";
import { ProfilePage } from "@/pages/save/ProfilePage";
import { SavePage } from "@/pages/save/SavePage";
import { ServicePage } from "@/pages/save/ServicePage";
import { ContactPage } from "@/pages/save/ContactPage";
import { PinboardPage } from "@/pages/save/PinboardPage";
import { GrowPage } from "@/pages/grow/GrowPage";
import { ElementAssessmentPage } from "@/pages/grow/ElementAssessmentPage";
import { ElementResultPage } from "@/pages/grow/ElementResultPage";
import { GrowPlayPage } from "@/pages/grow/GrowPlayPage";
import { GrowWatchPage } from "@/pages/grow/GrowWatchPage";
import { PowerDetailPage } from "@/pages/grow/PowerDetailPage";
import { PowerQuizPage } from "@/pages/grow/PowerQuizPage";
import { PowerQuizPartPage } from "@/pages/grow/PowerQuizPartPage";
import { PowerLibraryPage } from "@/pages/grow/PowerLibraryPage";
import { QuestionnaireAssessmentPage } from "@/pages/grow/QuestionnaireAssessmentPage";
import { MatchPage } from "@/pages/match/MatchPage";
import { MatchDetailPage } from "@/pages/match/MatchDetailPage";
import { SharePage } from "@/pages/share/SharePage";
import { ShareCreatePage } from "@/pages/share/ShareCreatePage";
import { ShareItemDetailPage } from "@/pages/share/ShareItemDetailPage";
import { ShareMinePage } from "@/pages/share/ShareMinePage";
import { ExtendedSearchPage } from "@/pages/ExtendedSearchPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { UserSettingsPage } from "@/pages/UserSettingsPage";
import { LoginPage } from "@/pages/auth/LoginPage";

// React Router 7 data router. Two top-level scopes:
//   /auth/*          - login, callback. No guard.
//   /                - everything else, gated by requireAuth loader.
//
// The basename comes from runtime config so the same build serves
// `/`, `/smycles/`, etc. on different deployments without rebuilding.

export function makeRouter() {
  const cfg = getConfig();
  return createBrowserRouter(
    [
      // --- Auth scope (no guard) ---
      { path: "auth/login", element: <LoginPage /> },
      {
        path: "auth/callback",
        // The actual code exchange happens server-side at the BFF's
        // /auth/callback. If a browser somehow lands here after the
        // BFF already processed the callback, bounce to home.
        element: <Navigate to="/" replace />,
      },

      // --- Protected scope ---
      {
        path: "/",
        loader: requireAuth,
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },

          // Save
          { path: "save", element: <SavePage /> },
          { path: "save/basics", element: <ProfilePage /> },
          { path: "p/:id", element: <ProfilePage /> },
          { path: "save/documents", element: <ServicePage /> },
          { path: "save/contact", element: <ContactPage /> },
          { path: "save/pinboard", element: <PinboardPage /> },

          // Grow
          { path: "grow", element: <GrowPage /> },
          { path: "grow/watch", element: <GrowWatchPage /> },
          { path: "grow/play", element: <GrowPlayPage /> },
          { path: "grow/play/power-quiz", element: <PowerQuizPage /> },
          { path: "grow/play/power-quiz/:questionnaireId", element: <PowerQuizPartPage /> },
          { path: "grow/map", element: <PowerLibraryPage /> },
          { path: "grow/power/:abbr", element: <PowerDetailPage /> },
          {
            path: "grow/power/:abbr/questionnaire/:questionnaireId",
            element: <QuestionnaireAssessmentPage />,
          },
          { path: "grow/:elementId", element: <ElementAssessmentPage /> },
          { path: "grow/:elementId/result", element: <ElementResultPage /> },

          // Match
          { path: "match", element: <MatchPage /> },
          { path: "match/new", element: <Navigate to="/match" replace /> },
          { path: "match/:matchId", element: <MatchDetailPage /> },

          // Share
          { path: "share", element: <SharePage /> },
          { path: "share/create", element: <ShareCreatePage /> },
          { path: "share/edit/:itemId", element: <ShareCreatePage /> },
          // Share discovery lives in the extended search now (/extended-search
          // ?type=SHARE). /share/discover/:id is the item detail viewer.
          { path: "share/discover/:id", element: <ShareItemDetailPage /> },
          { path: "share/mine", element: <ShareMinePage /> },

          // Search
          { path: "extended-search", element: <ExtendedSearchPage /> },
          // Personal account settings: any authenticated user (parent requireAuth).
          { path: "settings", element: <UserSettingsPage /> },
          // System settings: gated to users with a system-settings privilege.
          // genesis still enforces every write server-side; requireSystemAccess
          // only keeps the page out of unprivileged hands.
          { path: "admin/settings", loader: requireSystemAccess, element: <SettingsPage /> },
          // Prototyp tobis: Ingest-Uebersicht und Trigger. Gleiche Huerde wie die
          // Systemeinstellungen, das BFF prueft die Rolle zusaetzlich serverseitig.
          { path: "admin/tobis", loader: requireSystemAccess, element: <TobisAdminPage /> },
          // Prototyp tobis: Register und Filmografien. Gleiche Huerde wie die
          // Pipeline-Seite, das BFF prueft die Session zusaetzlich serverseitig.
          // /tobis selbst ist keine eigene Flaeche: die Saeule besteht aus den
          // beiden Registern, damit die Navigation genau einen aktiven Eintrag
          // hervorhebt (ein Untereintrag auf dem Saeulen-Root waere fuer jede
          // Unterseite mit-aktiv).
          { path: "tobis", element: <Navigate to="/tobis/titles" replace /> },
          { path: "tobis/titles", loader: requireSystemAccess, element: <TobisTitlesPage /> },
          {
            path: "tobis/titles/:tvmazeId",
            loader: requireSystemAccess,
            element: <TobisTitlePage />,
          },
          { path: "tobis/people", loader: requireSystemAccess, element: <TobisTalentsPage /> },
          {
            path: "tobis/people/:personId",
            loader: requireSystemAccess,
            element: <TobisTalentPage />,
          },
          // Labor: KI-gestuetzte Vergleichsfilm-Suche ueber dem Register, im
          // Zuschnitt von MATCH (Landing mit bisherigen Untersuchungen +
          // "Neu", dann der Editor unter der id). TobisLabPage ist jetzt die
          // Landing, der bisherige Stepper-Inhalt liegt in TobisLabSessionPage.
          { path: "tobis/lab", loader: requireSystemAccess, element: <TobisLabPage /> },
          {
            path: "tobis/lab/:sessionId",
            loader: requireSystemAccess,
            element: <TobisLabSessionPage />,
          },
          // Markt-Dashboard: FFA-Kino + AGF-Streaming, ohne Titelbezug.
          { path: "tobis/market", loader: requireSystemAccess, element: <TobisMarketPage /> },
        ],
      },

      // 404 catch-all
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
    { basename: cfg.basePath === "/" ? undefined : cfg.basePath.replace(/\/$/, "") },
  );
}
